import React, { useState, useEffect } from 'react';
import { 
  Terminal, Copy, Check, Eye, EyeOff, 
  Activity, Info, ShieldCheck, Zap, Key, 
  Lock, Globe2, Box, Package, RefreshCw, 
  FileCode
} from 'lucide-react';
import api from '../services/api';
import copyToClipboard from '../utils/clipboard';
import '../styles/dashboard.css';

function Webhooks({ user, onStatusMessage }) {
  const [profile, setProfile] = useState(null);
  const [activeDeployments, setActiveDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedPath, setCopiedPath] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const webhookDeployments = activeDeployments.filter(dep => dep?.meta?.source === 'webhook');

  const loadData = async () => {
    try {
      const data = await api.getStatus();
      setProfile(data.profile || null);
      setActiveDeployments(data.activeDeployments || []);
    } catch (err) {
      console.error('Failed to load webhook data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, id) => {
    if (!text) return;
    copyToClipboard(text).then(() => {
      setCopiedPath(id);
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch((e) => {
      console.error('Copy failed', e);
      onStatusMessage && onStatusMessage('Copy failed', 'error');
    });
  };
  
  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      await api.generateApiKey();
      onStatusMessage('API Key generated successfully', 'success');
      loadData(); // Reload profile with new key
    } catch (err) {
      onStatusMessage('Error generating key: ' + err.message, 'error');
    } finally {
      setGeneratingKey(false);
    }
  };

  const username = user?.username || 'user';
  // Construct webhook URL from API base URL (respects .env configuration)
  const apiBase = process.env.REACT_APP_API_BASE_URL || '/api';
  const backendUrl = apiBase.replace('/api', ''); // Remove /api suffix
  const webhookUrl = `${backendUrl}/api/webhook/${username}/deploy`;
  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -F "file=@your-app.zip"`;

  if (loading && !profile) {
    return (
      <div className="dashboard-loading" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <RefreshCw className="animate-spin text-primary" size={40} />
        <span style={{ fontWeight: 800, color: 'var(--text-tertiary)' }}>Initializing API Environment...</span>
      </div>
    );
  }

  return (
    <div className="vercel-deploy-container">
      <div className="form-section-group">
        <div className="form-section-header" style={{ borderBottom: 'none', paddingBottom: '0' }}>
          <div className="form-section-title-group">
            <h2 className="form-section-title">Automated Deployments</h2>
            <p className="form-section-subtitle">Manage API keys and webhook endpoints for CI/CD integration.</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', minWidth: 0 }}>
            
            {/* Credentials Section */}
            <div className="vercel-premium-form">
              <div className="form-section-header">
                <div className="form-section-title-group">
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Key size={18} /> Access Credentials
                  </h3>
                </div>
              </div>

              <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="form-group">
                  <label><Globe2 size={14} /> Webhook Endpoint</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--surface-2)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <code style={{ flex: 1, fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 700, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{webhookUrl}</code>
                    <button 
                      className="action-btn-premium"
                      onClick={() => handleCopy(webhookUrl, 'webhook')}
                      style={{ height: '32px' }}
                    >
                      {copiedPath === 'webhook' ? <Check size={14} style={{ color: 'var(--success)' }} /> : <Copy size={14} />}
                      <span>{copiedPath === 'webhook' ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label><Lock size={14} /> Secret API Key</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--surface-2)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <ShieldCheck size={18} style={{ color: 'var(--text-muted)' }} />
                    <code style={{ flex: 1, fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-main)', letterSpacing: showApiKey ? 'normal' : '0.3em', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {showApiKey ? (profile?.apiKey || 'No key generated') : '••••••••••••••••••••••••••••••••'}
                    </code>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      {!profile?.apiKey && (
                        <button 
                          className="action-btn-premium primary"
                          onClick={handleGenerateKey}
                          disabled={generatingKey}
                          style={{ height: '32px', border: '1px solid var(--border-subtle)' }}
                        >
                          {generatingKey ? 'Creating...' : 'Generate Key'}
                        </button>
                      )}
                      {profile?.apiKey && (
                        <>
                          <button 
                            className="action-btn-premium"
                            onClick={() => setShowApiKey(!showApiKey)}
                            style={{ height: '32px', background: 'var(--surface)', color: 'var(--text-main)', border: '1px solid var(--border)' }}
                          >
                            {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button 
                            className="action-btn-premium"
                            onClick={() => handleCopy(profile?.apiKey, 'apikey')}
                            style={{ height: '32px', background: 'var(--surface)', color: 'var(--text-main)', border: '1px solid var(--border)' }}
                          >
                            {copiedPath === 'apikey' ? <Check size={16} style={{ color: 'var(--success)' }} /> : <Copy size={16} />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Integration Guide */}
            <div className="vercel-premium-form">
              <div className="form-section-header">
                <div className="form-section-title-group">
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Terminal size={18} /> Quick Start Integration
                  </h3>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem' }}>
                <div style={{ position: 'relative', background: 'var(--surface-2)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <pre style={{ margin: 0, color: 'var(--text-main)', fontSize: '0.85rem', overflowX: 'auto', fontFamily: 'monospace', lineHeight: 1.6 }}>
                    {curlExample}
                  </pre>
                  <button 
                    className="action-btn-premium"
                    onClick={() => handleCopy(curlExample, 'curl')}
                    style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', height: '32px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                  >
                    {copiedPath === 'curl' ? <Check size={16} style={{ color: 'var(--success)' }} /> : <Copy size={16} />}
                  </button>
                </div>

                <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>
                  <Info size={14} />
                  <span>Use this endpoint in your CI/CD pipelines (GitHub Actions, GitLab CI).</span>
                </div>
              </div>
            </div>

            {/* Config Docs */}
            <div className="vercel-premium-form">
              <div className="form-section-header">
                <div className="form-section-title-group">
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <FileCode size={18} /> Configuration (aibit.json)
                  </h3>
                </div>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Include this file at your project root to customize deployment parameters.
                </p>
                <div style={{ background: 'var(--surface-2)', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <pre style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-main)', fontFamily: 'monospace', lineHeight: 1.5, overflowX: 'auto' }}>
{`{
  "appName": "my-service",
  "internalPort": 8080,
  "publicPort": 80,
  "cpus": 0.5,
  "memory": "256m",
  "domain": "api.mydomain.com",
  "env": {
    "NODE_ENV": "production"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', minWidth: 0 }}>
            {/* Active Deployments */}
            <div className="vercel-premium-form" style={{ minHeight: '400px' }}>
              <div className="form-section-header">
                <div className="form-section-title-group">
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Activity size={18} /> Active Status
                  </h3>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem' }}>
                {webhookDeployments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-tertiary)' }}>
                    <Package size={32} style={{ opacity: 0.2, marginBottom: '1rem' }} />
                    <p style={{ fontSize: '0.85rem', fontWeight: 700 }}>No active webhooks</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {webhookDeployments.map(dep => {
                      const completedCount = (dep.milestones || []).filter(m => m.status === 'completed').length;
                      const progress = ((dep.milestones || []).length > 0) ? (completedCount / dep.milestones.length) * 100 : 0;
                      
                      return (
                        <div key={dep.id} style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '1rem', background: 'var(--surface)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div className="bar-icon" style={{ width: '32px', height: '32px', borderRadius: '8px' }}>
                              <Box size={16} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dep.meta?.appName}</div>
                              <div style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', fontWeight: 700 }}>{dep.id.split('-').pop()}</div>
                            </div>
                          </div>
                          
                          <div style={{ height: '4px', background: 'var(--surface-3)', borderRadius: '99px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${progress}%`, background: 'var(--text-main)', transition: 'width 0.4s ease' }}></div>
                          </div>
                          
                          {dep.status === 'failed' && (
                            <div style={{ marginTop: '0.75rem', padding: '0.5rem', borderRadius: '6px', background: 'rgba(var(--danger-rgb), 0.1)', color: 'var(--danger)', fontSize: '0.75rem', fontWeight: 700 }}>
                              Failed: {dep.error?.message || 'Error'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ padding: '1.5rem', borderRadius: '20px', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                 <Zap size={16} /> Auto-Detection
              </h4>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5, fontWeight: 500 }}>
                The system automatically identifies artifacts. Zips are deployed as isolated services. Ensure your zip has entry points like index.html or server.js.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Webhooks;
;
